/**
 * Bundle Quantity Selector
 * Integrates with existing product form for add to cart
 */

class BundleSelector {
  constructor(container) {
    this.container = container;
    this.bundleId = container.id;
    this.productId = container.dataset.productId;
    this.basePrice = parseInt(container.dataset.basePrice, 10);

    // Load product data - prefer theme's product JSON for complete variant data with media
    const themeProductJson = document.querySelector('[data-product-json]');
    let productData = null;
    if (themeProductJson) {
      try {
        productData = JSON.parse(themeProductJson.textContent);
      } catch (e) {
        console.warn('Failed to parse theme product JSON:', e);
      }
    }

    // Fall back to bundle-specific JSON if theme JSON not available
    this.variants = productData?.variants || JSON.parse(
      document.querySelector(`[data-bundle-variants="${this.bundleId}"]`)?.textContent || '[]'
    );
    this.options = JSON.parse(
      document.querySelector(`[data-bundle-options="${this.bundleId}"]`)?.textContent || '[]'
    );
    this.config = JSON.parse(
      document.querySelector(`[data-bundle-config="${this.bundleId}"]`)?.textContent || '{}'
    );

    // Store product media and images for image lookups
    this.productMedia = productData?.media || [];
    this.productImages = productData?.images || [];

    // State
    this.selectedTier = 1;
    this.slotVariants = {};
    this.tierHeaderVariants = {}; // Track which variant each tier header is showing
    this.isSubmitting = false;

    // Elements
    this.tierInputs = container.querySelectorAll('[data-tier-input]');

    // Find the product form
    this.productForm = document.querySelector(`#${this.config.formId}`) ||
                       document.querySelector(`form[action*="/cart/add"]`);

    // Cache submit button
    this.submitButton = this.productForm?.querySelector('[type="submit"]');

    // Cache cart bar button for syncing
    this.cartBarButton = document.querySelector('#cart-bar .product__submit__add');

    this.init();
  }

  init() {
    this.initTierSelection();
    this.initVariantSelects();
    this.initializeSlotVariants();
    this.interceptFormSubmit();
    this.syncFormState();

    // Sync initial variant with theme
    const initialVariantId = this.slotVariants['1'];
    if (initialVariantId) {
      const variant = this.variants.find(v => v.id === initialVariantId);
      if (variant) {
        // Sync hidden option inputs immediately
        this.syncHiddenOptionInputs(variant);
        // Small delay to dispatch variant change after theme is fully initialized
        setTimeout(() => this.dispatchVariantChange(variant), 100);
      }
    }
  }

  initTierSelection() {
    this.tierInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.selectedTier = parseInt(e.target.value, 10);
        this.syncFormState();

        // Use the tier header's variant for gallery sync (not the first slot)
        const tierKey = this.selectedTier.toString();
        let variant = this.tierHeaderVariants[tierKey];

        // Fallback to first slot if tier header variant not set
        if (!variant) {
          const primarySlot = this.selectedTier === 1 ? '1' : `${this.selectedTier}-1`;
          const variantId = this.slotVariants[primarySlot];
          if (variantId) {
            variant = this.variants.find(v => v.id === variantId);
          }
        }

        if (variant) {
          this.dispatchVariantChange(variant);
        }
      });
    });
  }

  initVariantSelects() {
    const selects = this.container.querySelectorAll('[data-option-select]');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        const slot = select.dataset.slot;
        const tier = select.dataset.tier || (slot === '1' ? '1' : slot.split('-')[0]);
        const optionIndex = parseInt(select.dataset.optionIndex, 10);
        const variant = this.updateSlotVariant(slot);

        // Update the slot image
        this.updateSlotImage(slot);

        // Update the tier header image and tracking
        if (tier === '1' && variant) {
          // For tier 1, slot 1 IS the header, so just update tracking
          this.tierHeaderVariants['1'] = variant;
        } else if (variant) {
          // For tiers 2-4, update the header image
          this.updateTierHeaderImage(tier, variant);
        }

        // Cascade from Tier 1: set defaults for all other slots
        if (slot === '1') {
          this.cascadeVariantToSlots(slot, optionIndex, select.value);
        }

        // Update tier prices when any variant changes
        if (variant) {
          this.updateAllTierPrices();
        }

        this.syncFormState();

        // For slot 1, sync hidden option inputs and dispatch change for theme integration
        if (slot === '1' && variant) {
          this.syncHiddenOptionInputs(variant);
        }

        // Dispatch variant change event for theme integration (updates main gallery)
        if (variant) {
          this.dispatchVariantChange(variant);
        }
      });
    });
  }

  /**
   * Cascade variant selection from Tier 1 to all other slots
   * - Tier 1 (slot "1") → cascades to all other slots (sets defaults while browsing)
   * - All other slots → no cascade (user is configuring their mix & match bundle)
   */
  cascadeVariantToSlots(sourceSlot, optionIndex, value) {
    // Only Tier 1 cascades
    if (sourceSlot !== '1') return;

    const targetSlots = ['2-1', '2-2', '3-1', '3-2', '3-3', '4-1', '4-2', '4-3', '4-4'];

    for (const slot of targetSlots) {
      // Find the select element for this slot and option
      const select = this.container.querySelector(
        `[data-option-select][data-slot="${slot}"][data-option-index="${optionIndex}"]`
      );

      if (select && select.value !== value) {
        // Check if the value exists in this select's options
        const optionExists = Array.from(select.options).some(opt => opt.value === value);
        if (optionExists) {
          select.value = value;

          // Update the slot's variant
          const variant = this.updateSlotVariant(slot);

          // Update slot image
          this.updateSlotImage(slot);

          // Update tier header image for first slot of each tier
          if (slot.endsWith('-1') && variant) {
            const tier = slot.split('-')[0];
            this.updateTierHeaderImage(tier, variant);
          }
        }
      }
    }
  }

  initializeSlotVariants() {
    // Get all unique slots from the select elements
    const allSlots = new Set();
    this.container.querySelectorAll('[data-slot]').forEach(el => {
      allSlots.add(el.dataset.slot);
    });

    // If no slots found (product has no real options), use default variant for all tiers
    if (allSlots.size === 0 && this.variants.length > 0) {
      const defaultVariant = this.variants[0];
      this.slotVariants['1'] = defaultVariant.id;
      for (let tier = 2; tier <= 4; tier++) {
        for (let slot = 1; slot <= tier; slot++) {
          this.slotVariants[`${tier}-${slot}`] = defaultVariant.id;
        }
      }
      this.tierHeaderVariants['1'] = defaultVariant;
      return;
    }

    // Initialize each slot by reading current select values
    allSlots.forEach(slot => {
      this.updateSlotVariant(slot);
      // Update slot image to match selected variant
      this.updateSlotImage(slot);
    });

    // Set tier 1 header variant (uses slot 1's variant)
    const slot1VariantId = this.slotVariants['1'];
    if (slot1VariantId) {
      const slot1Variant = this.variants.find(v => v.id === slot1VariantId);
      if (slot1Variant) {
        this.tierHeaderVariants['1'] = slot1Variant;
      }
    }

    // Update tier header images for tiers 2-4 based on their first slot
    for (let tier = 2; tier <= 4; tier++) {
      const firstSlotVariantId = this.slotVariants[`${tier}-1`];
      if (firstSlotVariantId) {
        const variant = this.variants.find(v => v.id === firstSlotVariantId);
        if (variant) {
          this.updateTierHeaderImage(tier.toString(), variant);
        }
      }
    }
  }

  updateSlotVariant(slot) {
    const selects = this.container.querySelectorAll(`[data-slot="${slot}"]`);
    const selectedOptions = [];

    selects.forEach(select => {
      const optionIndex = parseInt(select.dataset.optionIndex, 10);
      selectedOptions[optionIndex] = select.value;
    });

    const variant = this.findVariant(selectedOptions);
    this.slotVariants[slot] = variant?.id || null;

    const input = this.container.querySelector(`[data-variant-id-slot="${slot}"]`);
    if (input) input.value = variant?.id || '';

    return variant;
  }

  findVariant(selectedOptions) {
    return this.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant.options[index] === option;
      });
    });
  }

  updateSlotImage(slot) {
    const variantId = this.slotVariants[slot];
    const variant = this.variants.find(v => v.id === variantId);
    const img = this.container.querySelector(`[data-slot-image="${slot}"]`);

    if (!img || !variant) return;

    const imageSrc = this.getVariantImageSrc(variant);
    if (imageSrc) {
      const imageContainer = img.closest('.bundle-slot-image');
      if (imageContainer) {
        imageContainer.classList.add('is-loading');
      }

      const newImg = new Image();
      newImg.onload = () => {
        img.src = this.resizeImageUrl(imageSrc, 128);
        if (imageContainer) {
          imageContainer.classList.remove('is-loading');
        }
      };
      newImg.onerror = () => {
        if (imageContainer) {
          imageContainer.classList.remove('is-loading');
        }
      };
      newImg.src = this.resizeImageUrl(imageSrc, 128);
    }
  }

  /**
   * Update the tier header image (the main image shown in the tier card header)
   */
  updateTierHeaderImage(tier, variant) {
    const img = this.container.querySelector(`[data-tier-header-image="${tier}"]`);
    if (!img || !variant) return;

    // Track which variant this tier header is showing
    this.tierHeaderVariants[tier] = variant;

    const imageSrc = this.getVariantImageSrc(variant);
    if (imageSrc) {
      const imageContainer = img.closest('.bundle-tier-image');
      if (imageContainer) {
        imageContainer.classList.add('is-loading');
      }

      const newImg = new Image();
      newImg.onload = () => {
        img.src = this.resizeImageUrl(imageSrc, 128);
        if (imageContainer) {
          imageContainer.classList.remove('is-loading');
        }
      };
      newImg.onerror = () => {
        if (imageContainer) {
          imageContainer.classList.remove('is-loading');
        }
      };
      newImg.src = this.resizeImageUrl(imageSrc, 128);
    }
  }

  /**
   * Resize a Shopify image URL to the specified width
   */
  resizeImageUrl(url, width) {
    if (!url) return url;

    // Handle Shopify CDN URLs with width parameter
    if (url.includes('width=')) {
      return url.replace(/width=\d+/, `width=${width}`);
    }

    // Handle Shopify CDN URLs without width parameter - add it
    if (url.includes('cdn.shopify.com')) {
      const separator = url.includes('?') ? '&' : '?';
      return url + separator + `width=${width}`;
    }

    // Fallback: try the legacy _WIDTHx format
    return url.replace(/(_\d+x\d*)?(\.[^.?]+)(\?|$)/, `_${width}x$2$3`);
  }

  /**
   * Get image source URL from variant data
   */
  getVariantImageSrc(variant) {
    if (!variant) return null;

    // Method 1: Direct src on featured_image (most common in Shopify JSON)
    if (variant.featured_image?.src) {
      return variant.featured_image.src;
    }

    // Method 2: Preview image from featured_media
    if (variant.featured_media?.preview_image?.src) {
      return variant.featured_media.preview_image.src;
    }

    // Method 3: Look up in product images by image_id or featured_image.id
    const imageId = variant.image_id || variant.featured_image?.id;
    if (imageId && this.productImages.length > 0) {
      const image = this.productImages.find(img => img.id === imageId);
      if (image?.src) return image.src;
    }

    // Method 4: Look up in product media by ID
    const mediaId = variant.featured_media?.id || imageId;
    if (mediaId && this.productMedia.length > 0) {
      const media = this.productMedia.find(m => m.id === mediaId);
      if (media?.src) return media.src;
      if (media?.preview_image?.src) return media.preview_image.src;
    }

    // Method 5: Look up by position in images array
    const position = variant.featured_image?.position || variant.featured_media?.position;
    if (position) {
      // Try images array first (1-indexed position)
      if (this.productImages.length >= position) {
        const image = this.productImages[position - 1];
        if (image?.src) return image.src;
      }
      // Try media array
      if (this.productMedia.length >= position) {
        const media = this.productMedia[position - 1];
        if (media?.src) return media.src;
        if (media?.preview_image?.src) return media.preview_image.src;
      }
    }

    // Method 6: Fallback - use first image if nothing else works
    if (this.productImages.length > 0 && this.productImages[0]?.src) {
      return this.productImages[0].src;
    }

    return null;
  }

  /**
   * Sync all form state: quantity, variant ID, button state, ATC price
   */
  syncFormState() {
    this.updateQuantityInput();
    this.updateFormVariant();
    this.updateButtonState();
    this.updateATCPrice();
  }

  /**
   * Sync hidden option inputs for theme ProductFormReader compatibility
   * These inputs trigger the theme's variant change detection
   */
  syncHiddenOptionInputs(variant) {
    if (!variant || !variant.options) return;

    const hiddenInputs = this.container.querySelectorAll('[data-bundle-option-input]');
    let changed = false;

    hiddenInputs.forEach(input => {
      const optionIndex = parseInt(input.dataset.optionIndex, 10);
      const newValue = variant.options[optionIndex];

      if (input.value !== newValue) {
        input.value = newValue;
        changed = true;
      }
    });

    // Dispatch change event on the last input to trigger ProductFormReader
    if (changed && hiddenInputs.length > 0) {
      const lastInput = hiddenInputs[hiddenInputs.length - 1];
      lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateQuantityInput() {
    // Update quantity input in the product form
    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = this.selectedTier;
    }
  }

  updateFormVariant() {
    // Get the primary variant ID based on current tier
    const primarySlot = this.selectedTier === 1 ? '1' : `${this.selectedTier}-1`;
    const variantId = this.slotVariants[primarySlot];

    // Update the variant ID in the product form
    const variantInput = this.productForm?.querySelector('input[name="id"]');
    if (variantInput && variantId) {
      const oldValue = variantInput.value;
      variantInput.value = variantId;

      // Dispatch change event if value actually changed (triggers theme's ProductFormReader)
      if (oldValue !== variantId.toString()) {
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Also update any variant selectors outside the bundle (for theme compatibility)
    const externalVariantInputs = document.querySelectorAll(`input[name="id"][form="${this.config.formId}"]`);
    externalVariantInputs.forEach(input => {
      if (variantId) input.value = variantId;
    });
  }

  /**
   * Check if all selected variants are available and update button state
   */
  updateButtonState() {
    if (!this.submitButton) return;

    const slots = this.getSelectedSlots();
    let allAvailable = true;
    let hasAllVariants = true;

    for (const slot of slots) {
      const variantId = this.slotVariants[slot];
      if (!variantId) {
        hasAllVariants = false;
        break;
      }
      const variant = this.variants.find(v => v.id === variantId);
      if (!variant || !variant.available) {
        allAvailable = false;
        break;
      }
    }

    // Update button state
    if (!hasAllVariants) {
      this.submitButton.disabled = true;
      if (this.cartBarButton) this.cartBarButton.disabled = true;
      this.setButtonText('Sélectionnez les options');
    } else if (!allAvailable) {
      this.submitButton.disabled = true;
      if (this.cartBarButton) this.cartBarButton.disabled = true;
      this.setButtonText('Rupture de stock');
    } else {
      this.submitButton.disabled = false;
      if (this.cartBarButton) this.cartBarButton.disabled = false;
      this.restoreButtonText();
    }
  }

  /**
   * Update the price displayed on the ATC button based on selected tier
   */
  updateATCPrice() {
    if (!this.submitButton) return;

    // Find all price elements and keep only one
    const allPriceElements = this.submitButton.querySelectorAll('.btn__price');

    // Remove duplicates - keep only the first one
    if (allPriceElements.length > 1) {
      for (let i = 1; i < allPriceElements.length; i++) {
        allPriceElements[i].remove();
      }
    }

    // Find price element - first try data-product-price, then fall back to .btn__price
    let priceElement = this.submitButton.querySelector('[data-product-price]');

    // If no data-product-price element, look for existing .btn__price
    if (!priceElement) {
      priceElement = this.submitButton.querySelector('.btn__price');
    }

    // If still no price element, create one inside the button text
    if (!priceElement) {
      const btnText = this.submitButton.querySelector('[data-add-to-cart-text]') ||
                      this.submitButton.querySelector('.btn__text');
      if (btnText) {
        priceElement = document.createElement('span');
        priceElement.className = 'btn__price';
        priceElement.setAttribute('data-product-price', '');
        btnText.appendChild(priceElement);
      }
    }

    if (!priceElement) return;

    // Get the price directly from the tier card to ensure it matches exactly
    const tierPriceElement = this.container.querySelector(`[data-tier-price="${this.selectedTier}"]`);
    const tierKey = this.selectedTier.toString();
    const discountPercent = this.config.discounts?.[tierKey] || 0;
    let formattedPrice;

    if (tierPriceElement) {
      // Use the exact same price text from the tier card
      formattedPrice = tierPriceElement.textContent.trim();
    } else {
      // Fallback: calculate using same formula as Liquid
      const quantity = this.selectedTier;
      const discountedPrice = Math.floor(this.basePrice * quantity * (100 - discountPercent) / 100);
      formattedPrice = this.formatMoney(discountedPrice);
    }

    priceElement.textContent = formattedPrice;

    // Ensure price inherits button text color (white on dark buttons)
    priceElement.style.color = 'inherit';

    // Add sale styling if discount applied
    if (discountPercent > 0) {
      priceElement.classList.add('product__price--sale');
    } else {
      priceElement.classList.remove('product__price--sale');
    }

    // Sync cart bar button price
    if (this.cartBarButton) {
      let cartBarPriceElement = this.cartBarButton.querySelector('.btn__price');
      if (!cartBarPriceElement) {
        const btnText = this.cartBarButton.querySelector('.btn__text');
        if (btnText) {
          cartBarPriceElement = document.createElement('span');
          cartBarPriceElement.className = 'btn__price';
          btnText.appendChild(cartBarPriceElement);
        }
      }
      if (cartBarPriceElement) {
        cartBarPriceElement.textContent = formattedPrice;
        cartBarPriceElement.style.color = 'inherit';
      }
    }
  }

  /**
   * Update all tier prices based on selected variants
   */
  updateAllTierPrices() {
    // Tier 1: Single item (slot "1")
    this.updateTierPrice(1, ['1']);

    // Tier 2: Two items (slots "2-1", "2-2")
    this.updateTierPrice(2, ['2-1', '2-2']);

    // Tier 3: Three items (slots "3-1", "3-2", "3-3")
    this.updateTierPrice(3, ['3-1', '3-2', '3-3']);

    // Tier 4: Four items (slots "4-1", "4-2", "4-3", "4-4")
    this.updateTierPrice(4, ['4-1', '4-2', '4-3', '4-4']);
  }

  /**
   * Update a single tier's price based on its selected variants
   */
  updateTierPrice(tier, slots) {
    const tierPriceEl = this.container.querySelector(`[data-tier-price="${tier}"]`);
    const tierCompareEl = this.container.querySelector(`[data-tier-card="${tier}"] .bundle-tier-compare`);
    const tierDiscountEl = this.container.querySelector(`[data-tier-card="${tier}"] .bundle-tier-discount`);

    if (!tierPriceEl) return;

    // Check if this is a Flash Sale product
    const isFlashSale = this.config.isFlashSale || false;
    const flashSaleDiscount = this.config.flashSaleDiscount || 50;

    // Calculate total price and compare price from selected variants
    let totalPrice = 0;
    let totalComparePrice = 0;
    let hasComparePrice = false;

    for (const slot of slots) {
      const variantId = this.slotVariants[slot];
      if (variantId) {
        const variant = this.variants.find(v => v.id === variantId);
        if (variant) {
          if (isFlashSale) {
            // Flash Sale: apply 50% discount to variant price
            const flashSalePrice = Math.floor(variant.price * (100 - flashSaleDiscount) / 100);
            totalPrice += flashSalePrice;
            // Compare price is the original full price
            totalComparePrice += variant.price;
            hasComparePrice = true;
          } else {
            totalPrice += variant.price;
            // Use compare_at_price if available, otherwise use price
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              totalComparePrice += variant.compare_at_price;
              hasComparePrice = true;
            } else {
              totalComparePrice += variant.price;
            }
          }
        }
      }
    }

    // If no variants selected yet, fall back to base price calculation
    if (totalPrice === 0) {
      totalPrice = this.basePrice * slots.length;
      totalComparePrice = isFlashSale ? (this.basePrice * 2 * slots.length) : (this.basePrice * slots.length);
    }

    // Get discount for this tier (0 for Flash Sale products)
    const tierDiscountPercent = this.config.discounts?.[tier.toString()] || 0;
    const discountedPrice = Math.floor(totalPrice * (100 - tierDiscountPercent) / 100);

    // Update tier price
    tierPriceEl.textContent = this.formatMoney(discountedPrice);

    // Update compare price
    if (tierCompareEl) {
      if (hasComparePrice || tierDiscountPercent > 0 || isFlashSale) {
        tierCompareEl.textContent = this.formatMoney(totalComparePrice);
        tierCompareEl.style.display = '';
      } else {
        tierCompareEl.style.display = 'none';
      }
    }

    // Update discount percentage badge
    if (tierDiscountEl) {
      if (isFlashSale) {
        // Flash Sale always shows -50%
        tierDiscountEl.textContent = `-${flashSaleDiscount}%`;
        tierDiscountEl.style.display = '';
      } else {
        // Calculate total discount: (compare_price - discounted_price) / compare_price * 100
        const totalDiscountPercent = Math.round((totalComparePrice - discountedPrice) / totalComparePrice * 100);

        if (totalDiscountPercent > 0) {
          tierDiscountEl.textContent = `-${totalDiscountPercent}%`;
          tierDiscountEl.style.display = '';
        } else {
          tierDiscountEl.style.display = 'none';
        }
      }
    }
  }

  /**
   * Format money in store's currency format
   */
  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }

    // Use theme's money format if available
    if (typeof theme !== 'undefined' && theme.moneyFormat) {
      return this.formatWithPattern(cents, theme.moneyFormat);
    }

    // Use config currency format if available
    if (this.config.currency) {
      return this.formatWithPattern(cents, this.config.currency);
    }

    // Fallback: format as euros (common for French stores)
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return amount + ' €';
  }

  /**
   * Format cents with a money pattern (e.g., "€{{amount}}" or "{{ amount }}")
   */
  formatWithPattern(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimals = Math.floor(cents / 100).toString();

    // Handle patterns with or without spaces (e.g., {{amount}} or {{ amount }})
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals);
  }

  setButtonText(text) {
    if (!this.originalButtonText) {
      this.originalButtonText = this.submitButton.innerHTML;
    }
    this.submitButton.innerHTML = `<span>${text}</span>`;

    // Sync cart bar button
    if (this.cartBarButton) {
      if (!this.originalCartBarButtonText) {
        this.originalCartBarButtonText = this.cartBarButton.innerHTML;
      }
      this.cartBarButton.innerHTML = `<span class="btn__text">${text}</span>`;
      this.cartBarButton.disabled = true;
    }
  }

  restoreButtonText() {
    if (this.originalButtonText) {
      this.submitButton.innerHTML = this.originalButtonText;
    }

    // Sync cart bar button
    if (this.cartBarButton && this.originalCartBarButtonText) {
      this.cartBarButton.innerHTML = this.originalCartBarButtonText;
      this.cartBarButton.disabled = false;
    }
  }

  /**
   * Dispatch variant change event for theme integration (prices, images, etc.)
   */
  dispatchVariantChange(variant) {
    // Find the product section container
    const productSection = this.container.closest('[data-section-type]') ||
                          this.container.closest('.product') ||
                          this.productForm?.closest('.product') ||
                          document.querySelector('.product');

    // Dispatch theme:variant:change for price/image updates (theme event)
    if (productSection) {
      productSection.dispatchEvent(
        new CustomEvent('theme:variant:change', {
          detail: {
            variant: variant,
          },
          bubbles: true,
        })
      );
    }

    // Also dispatch on document for any global listeners
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: {
        variant: variant,
        product: { id: this.productId }
      }
    }));

    // Directly update main product image if variant has featured media
    this.updateMainProductImage(variant);

    // Update URL for tier 1 (single item) only
    if (this.selectedTier === 1 && variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Update the main product gallery to show the variant's image
   */
  updateMainProductImage(variant) {
    if (!variant) return;

    const mediaId = variant.featured_media?.id || variant.featured_image?.id || variant.image_id;

    // Find the product-images container within the same section or fall back to any on the page
    const section = this.container.closest('[data-section-type]') ||
                    this.container.closest('.product') ||
                    this.productForm?.closest('[data-section-type]') ||
                    this.productForm?.closest('.product');
    const productImagesContainer = section?.querySelector('product-images') || document.querySelector('product-images');

    if (!productImagesContainer) return;

    // Method 1: Find by data-image-id (raw media ID)
    if (mediaId) {
      const directMatch = productImagesContainer.querySelector(`[data-image-id="${mediaId}"]`);
      if (directMatch?.dataset.mediaId) {
        productImagesContainer.dispatchEvent(
          new CustomEvent('theme:media:select', {
            bubbles: false,
            detail: { id: directMatch.dataset.mediaId },
          })
        );
        return;
      }
    }

    // Method 2: Find by data-media-id suffix (section-prefixed IDs)
    if (mediaId) {
      const allMediaElements = productImagesContainer.querySelectorAll('[data-media-id]');
      for (const el of allMediaElements) {
        const elMediaId = el.dataset.mediaId || '';
        if (elMediaId === mediaId.toString() ||
            elMediaId.endsWith('-' + mediaId) ||
            elMediaId.endsWith('-' + mediaId.toString())) {
          productImagesContainer.dispatchEvent(
            new CustomEvent('theme:media:select', {
              bubbles: false,
              detail: { id: elMediaId },
            })
          );
          return;
        }
      }
    }

    // Method 3: Find by image src matching (fallback)
    const imageSrc = this.getVariantImageSrc(variant);
    if (imageSrc) {
      const filename = imageSrc.split('/').pop().split('?')[0].replace(/_\d+x\d*/, '').replace(/\.[^.]+$/, '');
      const galleryImages = productImagesContainer.querySelectorAll('[data-media-id]');
      for (const container of galleryImages) {
        const imgElement = container.querySelector('img') || container;
        const imgSrc = imgElement.src || imgElement.dataset.src || '';
        if (imgSrc.includes(filename) && container.dataset.mediaId) {
          productImagesContainer.dispatchEvent(
            new CustomEvent('theme:media:select', {
              bubbles: false,
              detail: { id: container.dataset.mediaId },
            })
          );
          return;
        }
      }
    }
  }

  getSelectedSlots() {
    const tier = this.selectedTier;
    const slots = [];

    if (tier === 1) {
      slots.push('1');
    } else {
      for (let i = 1; i <= tier; i++) {
        slots.push(`${tier}-${i}`);
      }
    }

    return slots;
  }

  interceptFormSubmit() {
    if (!this.productForm) return;

    // The theme's product-form component intercepts button clicks and dispatches theme:cart:add
    // We need to intercept before the theme's handler runs
    // Strategy: Replace the button's click handlers by cloning and replacing the button
    if (this.submitButton) {
      const newButton = this.submitButton.cloneNode(true);
      this.submitButton.parentNode.replaceChild(newButton, this.submitButton);
      this.submitButton = newButton;

      // Add our click handler to the new button (no existing handlers)
      this.submitButton.addEventListener('click', async (e) => {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        e.preventDefault();
        e.stopPropagation();

        // For tier 1, dispatch the theme event to let the theme handle it normally
        if (this.selectedTier === 1) {
          // Ensure variant is set
          const variantInput = this.productForm.querySelector('input[name="id"]');
          if (variantInput && this.slotVariants['1']) {
            variantInput.value = this.slotVariants['1'];
          }
          // Dispatch theme:cart:add to let theme handle single item add
          document.dispatchEvent(
            new CustomEvent('theme:cart:add', {
              detail: { button: this.submitButton },
              bubbles: false,
            })
          );
          this.isSubmitting = false;
          return;
        }

        // For tier > 1, handle bundle add ourselves
        await this.addBundleToCart();
      });
    }

    // Also handle form submit as a fallback (in case theme doesn't intercept clicks)
    this.productForm.addEventListener('submit', async (e) => {
      // Only intercept if we have multiple items (tier > 1)
      if (this.selectedTier > 1) {
        e.preventDefault();
        e.stopPropagation();
        await this.addBundleToCart();
        return false;
      }
      // For tier 1, ensure variant is set before form submits
      const variantInput = this.productForm.querySelector('input[name="id"]');
      if (variantInput && this.slotVariants['1']) {
        variantInput.value = this.slotVariants['1'];
      }
    });
  }

  async addBundleToCart() {
    const originalText = this.submitButton?.innerHTML;
    const originalCartBarText = this.cartBarButton?.innerHTML;

    // Show loading state (same as theme's addToCart)
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('is-loading');
    }
    if (this.cartBarButton) {
      this.cartBarButton.disabled = true;
      this.cartBarButton.classList.add('is-loading');
    }

    try {
      const slots = this.getSelectedSlots();
      const discountPercent = this.config.discounts?.[this.selectedTier] || 0;
      const bundleId = this.bundleId + '-' + Date.now();

      // Group items by variant ID to combine quantities
      const itemsMap = new Map();

      for (const slot of slots) {
        const variantId = this.slotVariants[slot];
        if (!variantId) {
          throw new Error('Veuillez sélectionner toutes les options');
        }

        const variant = this.variants.find(v => v.id === variantId);
        if (!variant || !variant.available) {
          throw new Error('Une variante sélectionnée n\'est plus disponible');
        }

        // Combine quantities for identical variants
        if (itemsMap.has(variantId)) {
          itemsMap.get(variantId).quantity += 1;
        } else {
          itemsMap.set(variantId, {
            id: variantId,
            quantity: 1,
            properties: {
              '_bundle_id': bundleId,
              '_bundle_discount': discountPercent + '%'
            }
          });
        }
      }

      const items = Array.from(itemsMap.values());

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur lors de l\'ajout au panier');
      }

      const responseData = await response.json();

      // Success - match theme's animation behavior exactly
      if (this.submitButton) {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.classList.add('is-added');
        this.submitButton.disabled = false;

        // Dispatch theme:product:add event (same as tier 1) to trigger cart drawer animation
        this.submitButton.dispatchEvent(
          new CustomEvent('theme:product:add', {
            detail: {
              response: responseData,
              button: this.submitButton,
            },
            bubbles: true,
          })
        );
      }

      // Sync cart bar button animation
      if (this.cartBarButton) {
        this.cartBarButton.classList.remove('is-loading');
        this.cartBarButton.classList.add('is-added');
        this.cartBarButton.disabled = false;
      }

      // Try to open cart drawer or update cart
      this.handleCartSuccess(originalText, originalCartBarText);

    } catch (error) {
      console.error('Bundle add to cart error:', error);
      alert(error.message);

      // Reset buttons on error
      if (this.submitButton) {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.disabled = false;
        this.submitButton.innerHTML = originalText;
      }
      if (this.cartBarButton) {
        this.cartBarButton.classList.remove('is-loading');
        this.cartBarButton.disabled = false;
        this.cartBarButton.innerHTML = originalCartBarText;
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  async handleCartSuccess(originalText, originalCartBarText) {
    try {
      // Update cart count
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Restore button text after animation completes (same timing as theme)
      setTimeout(() => {
        if (this.submitButton && originalText) {
          this.submitButton.classList.remove('is-added');
          this.submitButton.innerHTML = originalText;
        }
        if (this.cartBarButton && originalCartBarText) {
          this.cartBarButton.classList.remove('is-added');
          this.cartBarButton.innerHTML = originalCartBarText;
        }
        // Re-sync the ATC price since we restored the original HTML
        this.updateATCPrice();
      }, 1000);
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bundle-selector]').forEach(container => {
    new BundleSelector(container);
  });
});

// Re-initialize on Shopify section reload
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-bundle-selector]').forEach(container => {
      new BundleSelector(container);
    });
  });
}
